import { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { Form } from 'antd';
import {
  DarwinCheckbox,
  DarwinDivider,
  DarwinFlex,
  DarwinSelect,
  DarwinTooltip,
} from 'components/darwin-ui';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from './index.module.css';
import { EUDR_HS_CODES, EUDR_PARTIAL_PREFIX } from 'constants/eudr.constants';
import Icon from 'components/darwin-ui/Icon';
import { CreateProductWithoutFormData } from 'types/productsTypes';
import { EudrHsCodeItem, EudrHsCodesMap } from 'types/eudrTypes';

const EUDR_FIELDS = {
  isEudrEnabled: 'isEudrEnabled' as const,
  hsCode: 'hsCode' as const,
} as const;

const { Option } = DarwinSelect;

interface EUDRSectionProps {
  control: Control<CreateProductWithoutFormData>;
  errors: FieldErrors<CreateProductWithoutFormData>;
  setValue: UseFormSetValue<CreateProductWithoutFormData>;
  isViewForm: boolean;
}

type HsOption = {
  id: string;
  label: string;
  disabled?: boolean;
  className?: string;
};

export const EUDRSection = ({
  control,
  errors,
  setValue,
  isViewForm,
}: EUDRSectionProps) => {
  const { t } = useTranslation();

  const isEudrEnabled = useWatch({
    control,
    name: EUDR_FIELDS.isEudrEnabled,
  });

  const hsCodeOptions = useMemo<HsOption[]>(() => {
    return Object.entries(EUDR_HS_CODES as EudrHsCodesMap).flatMap(
      ([category, items]) => {
        const header: HsOption = {
          id: `__header__${category}`,
          label: t(`eudr.headers.${category}`),
          disabled: true,
          className: styles.dropdownGroupLabel,
        };

        const children: HsOption[] = items.map((item: EudrHsCodeItem) => ({
          id: item.isPartial ? `${EUDR_PARTIAL_PREFIX}${item.code}` : item.code,
          label: `${item.isPartial ? EUDR_PARTIAL_PREFIX : ''}${
            item.code
          } - ${t(item.translationKey)}`,
        }));

        return [header, ...children];
      }
    );
  }, [t]);

  const hsCodeError = errors.hsCode;

  return (
    <DarwinFlex className={styles.contentContainer} vertical>
      <DarwinFlex className={styles.titleContainer}>
        <Icon name="tree" className={styles.titleIcon} size="lg" />
        <DarwinFlex vertical>
          <span className={styles.title}>
            {t('product.new-product-form.eudr-title')}
          </span>
          <span className={styles.subtitle}>
            {t('product.new-product-form.eudr-subtitle')}
          </span>
        </DarwinFlex>
      </DarwinFlex>

      <DarwinDivider style={{ margin: 0 }} />

      <DarwinFlex className={styles.checkboxContainer}>
        <Controller
          name={EUDR_FIELDS.isEudrEnabled}
          control={control}
          render={({ field }) => (
            <DarwinCheckbox
              checked={!!field.value}
              onChange={e => {
                const isChecked = e.target.checked;
                field.onChange(isChecked);

                // Clear hsCode when disabling EUDR
                if (!isChecked) {
                  setValue(EUDR_FIELDS.hsCode, '');
                }
              }}
              disabled={isViewForm}
            >
              <span className={styles.checkboxLabel}>
                {t('product.new-product-form.eudr-regulation')}
              </span>
            </DarwinCheckbox>
          )}
        />
        <DarwinTooltip
          title={t('product.new-product-form.eudr-checkbox-tooltip')}
        >
          <InfoCircleOutlined aria-label="EUDR info" />
        </DarwinTooltip>
      </DarwinFlex>

      <Form.Item
        style={{ display: isEudrEnabled ? undefined : 'none' }}
        label={t('product.new-product-form.hs-code')}
        validateStatus={hsCodeError ? 'error' : undefined}
        help={hsCodeError?.message ? String(hsCodeError.message) : undefined}
        className={styles.formInputTitle}
        required={isEudrEnabled}
      >
        <Controller
          name={EUDR_FIELDS.hsCode}
          control={control}
          rules={{
            validate: value => {
              if (!isEudrEnabled) {
                return true;
              }

              if (!value || value.startsWith('__header__')) {
                return t('product.new-product-form.hs-code-required');
              }

              return true;
            },
          }}
          render={({ field }) => (
            <DarwinSelect
              {...field}
              disabled={isViewForm}
              optionFilterProp="label"
              showSearch
              allowClear
              filterOption={(input, option) => {
                if (!option || option.disabled) return false;
                return String(option.label)
                  .toLowerCase()
                  .includes(input.toLowerCase());
              }}
            >
              {hsCodeOptions.map(item => (
                <Option
                  key={item.id}
                  value={item.id}
                  label={item.label}
                  disabled={item.disabled}
                  className={item.className}
                >
                  {item.label}
                </Option>
              ))}
            </DarwinSelect>
          )}
        />
      </Form.Item>
    </DarwinFlex>
  );
};
